import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, Renderer2, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { environment } from '../../../environments/environment';
import { MatIcon, MatIconRegistry } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { firstValueFrom, Subscription } from 'rxjs';
import { MarkdownModule } from 'ngx-markdown';
import { CommonExplainModule } from '../../common-explain/common-explain.module';
import { ToastMessageStackService } from '../../shared/services/toast-message-stack.service';
import { ApiExportService } from '../../shared/services/api/export-download/api-export.service';
import { ExportService } from '../../shared/services/export.service';
import { NgbAccordionModule, NgbModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import {
  SMART_SEARCH_SESSIONS_KEY,
  SmartSearchConfig,
  SmartSearchService,
  SmartSearchSource,
} from '../../shared/services/smart-search.service';
import {
  ResourceEntry,
  Resources,
  SmartSearchResource,
  SmartSearchResult,
  StepStatusEnum,
  TooltipHandler,
} from '../../models/smart-search';
import { Period } from '../../common-explain/models/filter-date';
import { ApiTopicService } from '../../shared/services/api/topic/api-topic.service';
import { SmartSearchPromptModule } from '../../shared/modules/smart-search-prompt/smart-search-prompt.module';
import { ModalTopicDeletedComponent } from '../modal-topic-deleted/modal-topic-deleted.component';
import { EventTypeName } from '../../models/user-tracker';
import { first } from 'rxjs/operators';
import { UserTrackerService } from '../../shared/services/tracking/user-tracker.service';

const MAX_VISIBLE_DOCS = 5;

interface ContextUpdateParams {
  impacterName?: string;
  impacterId?: string;
  prompt?: string;
  displayedPerimeter?: string;
  fullPerimeter?: string;
  period?: Period;
  topic?: string;
  keywords?: string;
  promptInputs?: Record<string, string>;
}

interface SmartSearchQuery {
  action: 'smart-search';
  api_token: string;
  task_id?: string;
  prompt?: string;
  prompt_base?: string;
  prompt_inputs?: Record<string, string>;
  prompt_id?: number;
  prompt_type?: 'default' | 'user';
  displayed_perimeter?: string;
  full_perimeter?: string;
  impacter_name?: string;
  filters?: {
    document_types?: string[];
    perimeter?: string[];
    territories?: string[];
    sources?: {
      article?: string[];
      admin_doc?: string[];
    };
    text?: string;
    topic_id?: number;
    impacter_id?: number;
    period?: Period;
  };
}

@Component({
  selector: 'app-smart-search',
  standalone: true,
  templateUrl: './smart-search.component.html',
  styleUrls: ['./smart-search.component.scss'],
  imports: [
    MatIcon,
    TranslateModule,
    NgxSkeletonLoaderModule,
    MarkdownModule,
    CommonModule,
    CommonExplainModule,
    NgbAccordionModule,
    NgbTooltip,
    SmartSearchPromptModule,
  ],
})
export class SmartSearchComponent implements OnInit, OnDestroy, AfterViewInit {
  result: SmartSearchResult | null = null;
  step: StepStatusEnum = StepStatusEnum.IN_PROGRESS;
  taskId: string | null = null;
  websocket: WebSocket | null = null;
  errorTitle: string | null = null;
  errorContent: string | null = null;

  readonly resourceTypes: SmartSearchSource[] = [SmartSearchSource.PRESS, SmartSearchSource.ADMIN_DOC, SmartSearchSource.WEB];

  resources: Resources = {
    [SmartSearchSource.PRESS]: {
      documents: [],
      showAllDocuments: false,
      icon: 'ph ph-newspaper ph-fill source-icon',
      toggle: () => this.toggleShowAll(SmartSearchSource.PRESS),
    },

    [SmartSearchSource.ADMIN_DOC]: {
      documents: [],
      showAllDocuments: false,
      icon: 'ph-fill ph-read-cv-logo',
      toggle: () => this.toggleShowAll(SmartSearchSource.ADMIN_DOC),
    },

    [SmartSearchSource.WEB]: {
      documents: [],
      showAllDocuments: false,
      icon: 'ph ph-globe',
      toggle: () => this.toggleShowAll(SmartSearchSource.WEB),
    },
  };

  get resourceEntries(): Array<{
    type: SmartSearchSource;
    entry: ResourceEntry;
  }> {
    return this.resourceTypes.map(type => ({
      type,
      entry: this.resources[type],
    }));
  }
  exportType: 'pdf' | 'docx' | null = null;
  className = 'toast-export-in-progress';
  showPromptContext = false;

  private getContextValue<K extends keyof SmartSearchConfig>(key: K): SmartSearchConfig[K] {
    return this.smartSearchService.context()[key];
  }

  get promptContent(): string {
    return (this.getContextValue('promptBase') as string) || '';
  }

  get topic(): string {
    return (this.getContextValue('topic') as string) || '';
  }

  get keywords(): string {
    return (this.getContextValue('keywords') as string) || '';
  }

  get periodStart(): string {
    return (this.getContextValue('period') as Period)?.from || '';
  }

  get periodEnd(): string {
    return (this.getContextValue('period') as Period)?.to || '';
  }

  get perimeter(): string {
    return (this.getContextValue('displayedPerimeter') as string) || '';
  }

  private queryParamsSubscription?: Subscription;
  private originalTitle = '';
  private blinkingInterval?: ReturnType<typeof globalThis.setInterval>;
  private blinkingTimeout?: ReturnType<typeof globalThis.setTimeout>;
  private tooltipHandlers: TooltipHandler[] = [];
  private visibilityChangeHandler?: () => void;
  private sessionContext: SmartSearchConfig = {};

  @ViewChild('synthesisContainer', { static: false })
  synthesisContainer?: ElementRef;
  @ViewChild('referenceBadge', { static: false, read: TemplateRef })
  referenceBadgeTemplate?: TemplateRef<any>;
  @ViewChild('SynthesisExportToast', { read: TemplateRef })
  synthesisExportToast!: TemplateRef<any>;
  @ViewChild('lightToast', { read: TemplateRef })
  lightToast!: TemplateRef<any>;
  @ViewChild('errorInExport', { read: TemplateRef })
  errorInExport!: TemplateRef<any>;

  protected readonly StepStatusEnum = StepStatusEnum;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private renderer: Renderer2,
    private translateService: TranslateService,
    private matIconRegistry: MatIconRegistry,
    private toastMessageStackService: ToastMessageStackService,
    private apiExportService: ApiExportService,
    private exportService: ExportService,
    private smartSearchService: SmartSearchService,
    private apiTopicService: ApiTopicService,
    private modalService: NgbModal,
    private userTrackerService: UserTrackerService
  ) {}

  ngOnInit(): void {
    this.originalTitle = document.title;
    this.updatePageTitle();
    this.setupVisibilityListener();
    this.loadSessionConfig();
    this.syncContextFromSession();
    this.queryParamsSubscription = this.route.queryParams.subscribe(async params => {
      if (this.websocket?.readyState === WebSocket.OPEN) {
        return;
      }
      this.taskId = params['taskId'] || null;
      if (this.taskId) {
        const s3Url = this.buildS3Url(this.taskId);
        const fetched = await this.tryFetchFromS3(s3Url);
        if (!fetched) {
          this.startSmartSearch();
        }
      } else {
        this.startSmartSearch();
      }
    });
  }

  ngAfterViewInit(): void {
    this.setupSourceTooltips();
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  // ========== Computed Properties ==========

  get totalDocuments(): number {
    return this.result?.stats?.nb_scored_documents ?? 0;
  }

  get articleCount(): number {
    return this.result?.stats?.nb_scored_per_doc_type?.article ?? 0;
  }

  get adminDocCount(): number {
    return this.result?.stats?.nb_scored_per_doc_type?.admin_doc ?? 0;
  }

  get hasDocumentsBreakdown(): boolean {
    return this.articleCount > 0 || this.adminDocCount > 0;
  }

  get formattedMinDate(): string {
    return this.result?.stats?.min_publication_date_scored ? this.formatDate(this.result.stats.min_publication_date_scored) : '';
  }

  get formattedMaxDate(): string {
    return this.result?.stats?.max_publication_date_scored ? this.formatDate(this.result.stats.max_publication_date_scored) : '';
  }

  get hasPeriod(): boolean {
    return !!(this.result?.stats?.min_publication_date_scored && this.result?.stats?.max_publication_date_scored);
  }

  get documentsPlural(): string {
    return this.totalDocuments > 1 ? 's' : '';
  }

  get isEmptySynthesis(): boolean {
    return this.step === StepStatusEnum.COMPLETED && !!this.result && !this.result.synthesis?.trim();
  }

  get shouldShowErrorState(): boolean {
    return (this.step === StepStatusEnum.ERROR && !!this.errorTitle) || this.isEmptySynthesis;
  }

  get isNoRelevantDocumentsError(): boolean {
    return !!this.errorTitle && this.errorTitle === 'search.smart-search-result.no-relevant-documents-title';
  }

  get totalResources(): number {
    return this.result?.stats?.nb_quoted_documents ?? 0;
  }

  // ========== Resource Lists ==========

  getVisibleDocs(sourceType: SmartSearchSource): any[] {
    const { documents, showAllDocuments } = this.resources[sourceType];
    return showAllDocuments ? documents : documents.slice(0, MAX_VISIBLE_DOCS);
  }

  hasMoreDocs(sourceType: SmartSearchSource): boolean {
    const { documents } = this.resources[sourceType];
    return documents.length > MAX_VISIBLE_DOCS;
  }

  // ========== Translation Keys ==========

  getSourceTranslationKey(sourceType: string, count: number): string {
    const isPlural = count > 1;
    const keys: Record<string, { singular: string; plural: string }> = {
      [SmartSearchSource.PRESS]: {
        singular: 'search.smart-search-result.article-of-press',
        plural: 'search.smart-search-result.articles-of-press',
      },
      [SmartSearchSource.ADMIN_DOC]: {
        singular: 'search.smart-search-result.administrative-publication',
        plural: 'search.smart-search-result.administrative-publications',
      },
      [SmartSearchSource.WEB]: {
        singular: 'search.smart-search-result.web-content',
        plural: 'search.smart-search-result.web-contents',
      },
    };

    const sourceKeys = keys[sourceType];
    if (!sourceKeys) {
      return '';
    }

    return isPlural ? sourceKeys.plural : sourceKeys.singular;
  }

  // ========== Public Methods ==========

  retry(): void {
    this.resetState();
    const sessionId = this.route.snapshot.queryParams['sessionId'];
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: sessionId ? { sessionId } : {},
      replaceUrl: true,
    });
    this.startSmartSearch();
  }

  relaunchWithUpdatedPrompt(event?: { prompt: string; promptWithValues: string; tagsUserInputs: Record<string, string> }): void {
    if (!event) {
      return;
    }
    if (!this.sessionContext || Object.keys(this.sessionContext).length === 0) {
      this.sessionContext = {
        ...this.smartSearchService.context(),
      };
    }

    const { prompt: updatedPrompt, promptWithValues, tagsUserInputs: promptInputs } = event;

    if (this.sessionContext.topicId) {
      firstValueFrom(this.apiTopicService.getTopic(this.sessionContext.topicId.toString()))
        .then(() => {
          this.openNewSearchTab(updatedPrompt, promptWithValues, promptInputs);
        })
        .catch(() => {
          this.showTopicDeletedModal();
        });
    } else {
      this.openNewSearchTab(updatedPrompt, promptWithValues, promptInputs);
    }
  }

  private openNewSearchTab(updatedPrompt: string, promptWithValues: string, promptInputs: Record<string, string>): void {
    const newSessionId = `search_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

    const newSessionContext = {
      ...this.sessionContext,
      prompt: promptWithValues,
      promptBase: updatedPrompt,
      promptInputs: promptInputs,
    };

    const sessionsStr = localStorage.getItem(SMART_SEARCH_SESSIONS_KEY) || '{}';
    const sessions = JSON.parse(sessionsStr);
    sessions[newSessionId] = newSessionContext;
    localStorage.setItem(SMART_SEARCH_SESSIONS_KEY, JSON.stringify(sessions));

    window.open(`/smart-search?sessionId=${newSessionId}`, '_blank');
  }

  private showTopicDeletedModal(): void {
    const topicName = this.topic || '';
    const modal = this.modalService.open(ModalTopicDeletedComponent, {
      centered: true,
      windowClass: 'topic-deleted-modal-dialog',
    });
    modal.componentInstance.title = 'search.smart-search-result.topic-deleted-modal-title';
    modal.componentInstance.content = 'search.smart-search-result.topic-deleted-modal-content';
    modal.componentInstance.topicName = topicName;
  }

  toggleShowAll(type: SmartSearchSource): void {
    this.resources[type].showAllDocuments = !this.resources[type].showAllDocuments;

    setTimeout(() => this.setupSourceTooltips(), 0);
  }

  truncateTitle(title: string, maxLength: number = 50): string {
    return title?.length > maxLength ? title.substring(0, maxLength) + '...' : title;
  }

  removeEmTags(html: string): string {
    if (!html) {
      return '';
    }
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    tempDiv.querySelectorAll('em').forEach(em => {
      const textNode = document.createTextNode(em.textContent || '');
      em.parentNode?.replaceChild(textNode, em);
    });
    return tempDiv.innerHTML;
  }

  getResourceIndex(resource: SmartSearchResource): number {
    if (!resource?.synthesis_index) return 0;

    const synthesisIndex = resource.synthesis_index;
    const parts = synthesisIndex.split('.');
    const mainIndex = Number.parseInt(parts[0], 10);
    return Number.isNaN(mainIndex) ? 0 : mainIndex;
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleDateString('fr-FR', { month: 'long' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  }

  openUrl(url: string): void {
    // Add taskId to URL if not already present (for admin docs from synthesis)
    // Get taskId from route params or component property
    const taskId = this.taskId || this.route.snapshot.queryParams['taskId'] || null;

    if (url && url.includes('/admin-doc/') && taskId) {
      const hasQueryParams = url.includes('?');
      const separator = hasQueryParams ? '&' : '?';
      url = `${url}${separator}taskId=${encodeURIComponent(taskId)}`;
    }
    window.open(url, '_blank', 'noreferrer');
  }

  getStepTranslationKey(): string {
    const stepKeys: Record<string, string> = {
      [StepStatusEnum.IN_PROGRESS]: 'search.smart-search-result.in-progress',
      [StepStatusEnum.WEBSOCKET_STARTED]: 'search.smart-search-result.websocket-started',
      [StepStatusEnum.AUTH]: 'search.smart-search-result.websocket-started',
      [StepStatusEnum.SCORING]: 'search.smart-search-result.scoring',
      [StepStatusEnum.SYNTHESIS]: 'search.smart-search-result.synthesis',
    };
    return stepKeys[this.step] || '';
  }

  getDocumentBreakdown(): string {
    const parts: string[] = [];
    if (this.articleCount > 0) {
      const translatedKey = this.translateService.instant(this.getSourceTranslationKey(SmartSearchSource.PRESS, this.articleCount));
      parts.push(`${this.articleCount} ${translatedKey}`);
    }
    if (this.adminDocCount > 0) {
      const translatedKey = this.translateService.instant(this.getSourceTranslationKey(SmartSearchSource.ADMIN_DOC, this.adminDocCount));
      parts.push(`${this.adminDocCount} ${translatedKey}`);
    }
    return parts.join(' et ');
  }

  onMarkdownRendered(event: any): void {
    const container = this.synthesisContainer?.nativeElement;
    if (!container || !this.result?.synthesis) {
      return;
    }

    const markdownElement =
      container.querySelector('markdown') || container.querySelector('.markdown-content') || container.firstElementChild;
    if (!markdownElement) {
      return;
    }

    markdownElement.innerHTML = this.convertSynthesisStyles(markdownElement.innerHTML);
    this.replaceReferencesWithButtons(markdownElement as HTMLElement);
    this.setupSynthesisTooltips(container);
    container.addEventListener('click', this.handleButtonClick);
  }

  // ========== WebSocket & Search ==========

  private startSmartSearch(): void {
    const query = this.buildSearchQuery();
    if (!query) {
      return;
    }

    this.resetState();
    this.step = StepStatusEnum.IN_PROGRESS;
    this.updatePageTitle();

    this.websocket = new WebSocket(environment.smartSearchApiGatewayUrl);
    this.websocket.addEventListener('open', () => {
      this.websocket?.send(JSON.stringify(query));
      this.step = StepStatusEnum.WEBSOCKET_STARTED;
      this.updatePageTitle();
    });

    this.websocket.addEventListener('error', () => {
      this.step = StepStatusEnum.ERROR;
      this.updatePageTitle();
    });

    this.websocket.addEventListener('message', event => {
      this.handleWebSocketMessage(JSON.parse(event.data));
    });
  }

  private buildSearchQuery(): SmartSearchQuery | null {
    const apiToken = localStorage.getItem('api_token');
    if (!apiToken) {
      console.error('No API token found');
      return null;
    }

    if (
      this.taskId &&
      (this.step == StepStatusEnum.COMPLETED || this.step == StepStatusEnum.ERROR || this.step == StepStatusEnum.IN_PROGRESS)
    ) {
      return {
        action: 'smart-search',
        task_id: this.taskId,
        api_token: apiToken,
      };
    }

    if (!this.sessionContext || Object.keys(this.sessionContext).length === 0) {
      console.error('No session context available');
      return null;
    }

    const query: SmartSearchQuery = {
      action: 'smart-search',
      api_token: apiToken,
      prompt: this.sessionContext.prompt,
      filters: {
        document_types: this.sessionContext.sources,
        perimeter: this.sessionContext.perimeter || [],
        territories: this.sessionContext.territories || [],
        sources: {
          article: this.sessionContext.pressSources || [],
          admin_doc: this.sessionContext.adminDocSources || [],
        },
      },
      prompt_base: this.sessionContext.promptBase,
      prompt_inputs: this.sessionContext.promptInputs,
      prompt_id: this.sessionContext.promptId,
      prompt_type: this.sessionContext.promptType,
      displayed_perimeter: this.sessionContext.displayedPerimeter,
      full_perimeter: this.sessionContext.fullPerimeter,
      impacter_name: this.sessionContext.impacterName || '',
    };

    if (this.sessionContext.keywords) {
      query.filters!.text = this.sessionContext.keywords;
    }
    if (this.sessionContext.topicId) {
      query.filters!.topic_id = this.sessionContext.topicId;
    }
    if (this.sessionContext.impacterId) {
      query.filters!.impacter_id = Number(this.sessionContext.impacterId);
      if (!this.sessionContext.keywords) {
        query.filters!.text = '';
      }
    }
    if (this.sessionContext.period) {
      query.filters!.period = this.sessionContext.period;
    }
    if (this.sessionContext.promptInputs) {
      query.prompt_inputs = { ...this.sessionContext.promptInputs };
    }

    return query;
  }

  private loadSessionConfig(): void {
    const sessionId = this.route.snapshot.queryParams['sessionId'];
    if (!sessionId) {
      console.error('No sessionId found in URL');
      return;
    }

    const sessionsStr = localStorage.getItem(SMART_SEARCH_SESSIONS_KEY) || '{}';
    const sessions = JSON.parse(sessionsStr);
    const session = sessions[sessionId];

    if (!session) {
      console.error('Session not found for sessionId:', sessionId);
      return;
    }

    this.sessionContext = session;
  }

  private syncContextFromSession(): void {
    if (!this.sessionContext || Object.keys(this.sessionContext).length === 0) {
      return;
    }

    this.updateContext({
      prompt: this.sessionContext.promptBase,
      displayedPerimeter: this.sessionContext.displayedPerimeter,
      fullPerimeter: this.sessionContext.fullPerimeter,
      period: this.sessionContext.period,
      topic: this.sessionContext.topic,
      keywords: this.sessionContext.keywords,
      impacterName: this.sessionContext?.impacterName ?? undefined,
      impacterId: this.sessionContext?.impacterId ?? undefined,
      promptInputs: this.sessionContext.promptInputs,
    });

    if (this.sessionContext.topicId && !this.sessionContext.topic) {
      firstValueFrom(this.apiTopicService.getTopic(this.sessionContext.topicId.toString()))
        .then(topic => {
          this.updateContext({
            prompt: this.sessionContext.promptBase,
            displayedPerimeter: this.sessionContext.displayedPerimeter,
            fullPerimeter: this.sessionContext.fullPerimeter,
            period: this.sessionContext.period,
            topic: topic.name,
            keywords: this.sessionContext.keywords,
            impacterName: this.sessionContext?.impacterName ?? undefined,
            impacterId: this.sessionContext?.impacterId ?? undefined,
            promptInputs: this.sessionContext.promptInputs,
          });
        })
        .catch(error => {
          console.error('Error loading topic:', error);
        });
    }
  }

  private handleWebSocketMessage(message: any): void {
    switch (message.type) {
      case 'step':
        this.step = message.message;
        this.updatePageTitle();
        break;
      case 'id':
        this.updateTaskIdInUrl(message.message);
        break;
      case 'result':
        this.handleResult(message.message);
        break;
      case 'result_url':
        this.fetchResultFromUrl(message.message);
        break;
      case 'error':
        this.handleError(message.message);
        break;
      case 'done':
        this.websocket?.close();
        this.step = StepStatusEnum.COMPLETED;
        this.updatePageTitle();
        break;
    }
  }

  private handleResult(message: any): void {
    if (typeof message === 'string') {
      try {
        this.result = JSON.parse(message);
      } catch {
        this.result = { synthesis: message };
      }
    } else {
      this.result = message;
    }
    this.updateFilteredResources();
    this.step = StepStatusEnum.COMPLETED;
    this.updatePageTitle();
    setTimeout(() => this.setupSourceTooltips(), 0);
  }

  private checkIsNoRelevantDocumentsError(error: any): boolean {
    return error === 'NO_RELEVANT_DOCUMENTS' || error === 'No relevant documents';
  }

  private handleError(message: any): void {
    if (this.checkIsNoRelevantDocumentsError(message)) {
      this.errorTitle = 'search.smart-search-result.no-relevant-documents-title';
      this.errorContent = 'search.smart-search-result.no-relevant-documents-content';
    } else {
      this.errorTitle = 'search.smart-search-result.error-default-title';
      this.errorContent = null;
    }

    this.step = StepStatusEnum.ERROR;
    this.websocket?.close();
    this.updatePageTitle();
  }

  private updateTaskIdInUrl(taskId: string): void {
    this.taskId = taskId;
    const sessionId = this.route.snapshot.queryParams['sessionId'];
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { taskId, sessionId },
      replaceUrl: true,
    });
  }

  private buildS3Url(taskId: string): string {
    const baseUrl = environment.s3SmartSearchBaseUrl;
    return `${baseUrl}/${taskId}.json`;
  }

  private async tryFetchFromS3(url: string): Promise<boolean> {
    try {
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();

        if (this.checkIsNoRelevantDocumentsError(data.error)) {
          this.handleError(data.error);
          return true;
        }

        this.result = data;

        this.updateContext({
          prompt: data.context.prompt_base || data.context.prompt,
          displayedPerimeter: data.context.displayed_perimeter,
          fullPerimeter: data.context.full_perimeter,
          period: data.context.filters.period,
          topic: data.context.filters.topic_name,
          keywords: data.context.filters.text,
          promptInputs: data.context.prompt_inputs,
          impacterId: data.context.filters.impacter_id,
          impacterName: data.context.impacter_name || null,
        });
        this.updateFilteredResources();
        this.step = StepStatusEnum.COMPLETED;
        this.updatePageTitle();
        setTimeout(() => this.setupSourceTooltips(), 0);
        this.trackSmartSearch(EventTypeName.SMART_SEARCH_OPENED);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error fetching from S3:', error);
      return false;
    }
  }

  private updateContext(params: ContextUpdateParams): void {
    const currentContext = this.smartSearchService.context();
    const updatedContext: SmartSearchConfig = {
      ...currentContext,
      ...(params.displayedPerimeter && { displayedPerimeter: params.displayedPerimeter }),
      ...(params.fullPerimeter && { fullPerimeter: params.fullPerimeter }),
      ...(params.period && { period: params.period }),
      ...(params.topic !== undefined && { topic: params.topic }),
      ...(params.keywords !== undefined && { keywords: params.keywords }),
      ...(params.impacterName !== undefined && { impacterName: params.impacterName }),
      ...(params.impacterId !== undefined && { impacterId: params.impacterId }),
      ...(params.prompt !== undefined && {
        promptBase: params.prompt,
        prompt: params.prompt,
      }),
      promptInputs: {
        ...currentContext.promptInputs,
        ...params.promptInputs,
      },
    };

    this.smartSearchService.context.set(updatedContext);
  }

  private async fetchResultFromUrl(url: string): Promise<void> {
    try {
      const response = await fetch(url);
      this.result = await response.json();
      this.updateFilteredResources();
      this.step = StepStatusEnum.COMPLETED;
      this.updatePageTitle();
      setTimeout(() => this.setupSourceTooltips(), 0);
    } catch (error) {
      console.error('Error fetching result:', error);
      this.step = StepStatusEnum.ERROR;
      this.updatePageTitle();
    } finally {
      this.websocket?.close();
    }
  }

  // ========== Page Title Management ==========

  private updatePageTitle(): void {
    this.stopBlinking();

    if (this.step === StepStatusEnum.ERROR) {
      document.title = this.translateService.instant('search.smart-search-result.page-title-error');
    } else if (this.step === StepStatusEnum.COMPLETED) {
      this.startBlinking();
    } else {
      document.title = this.translateService.instant('search.smart-search-result.page-title-in-progress');
    }
  }

  private startBlinking(): void {
    // If the tab is already visible, don't start blinking
    if (!document.hidden) {
      document.title = this.translateService.instant('search.smart-search-result.page-title-completed');
      return;
    }

    let isGreen = true;
    document.title = this.translateService.instant('search.smart-search-result.page-title-completed');

    this.blinkingInterval = globalThis.setInterval(() => {
      if (!document.hidden) {
        this.stopBlinking();
        document.title = this.translateService.instant('search.smart-search-result.page-title-completed');
        return;
      }
      isGreen = !isGreen;
      document.title = isGreen
        ? this.translateService.instant('search.smart-search-result.page-title-completed')
        : this.translateService.instant('search.smart-search-result.page-title-completed-alt');
    }, 500);

    this.blinkingTimeout = globalThis.setTimeout(() => {
      this.stopBlinking();
      document.title = this.translateService.instant('search.smart-search-result.page-title-completed');
    }, 30000);
  }

  private stopBlinking(): void {
    if (this.blinkingInterval) {
      clearInterval(this.blinkingInterval);
      this.blinkingInterval = undefined;
    }
    if (this.blinkingTimeout) {
      clearTimeout(this.blinkingTimeout);
      this.blinkingTimeout = undefined;
    }
  }

  private setupVisibilityListener(): void {
    this.visibilityChangeHandler = () => {
      if (!document.hidden) {
        this.stopBlinking();
        if (this.step === StepStatusEnum.COMPLETED) {
          document.title = this.translateService.instant('search.smart-search-result.page-title-completed');
        }
      }
    };
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
  }

  // ========== Resource Filtering ==========

  private getFilteredResources(docType: string): any[] {
    const allResources = this.result?.resources || [];
    const resources = allResources.filter(r => {
      return r.document_type === docType && r.synthesis_index;
    });
    const deduplicated = this.deduplicateByMainIndex(resources);
    return this.sortResourcesByIndex(deduplicated);
  }

  private deduplicateByMainIndex(resources: SmartSearchResource[]): any[] {
    const getSubIndex = (resource: SmartSearchResource): number => {
      const index = resource.synthesis_index ?? '';
      const parts = index.split('.');
      return parts.length > 1 ? Number.parseInt(parts[1], 10) : 0;
    };

    const seen = new Map<number, any>();

    for (const resource of resources) {
      const mainIndex = this.getResourceIndex(resource);
      const existing = seen.get(mainIndex);
      if (!existing) {
        seen.set(mainIndex, resource);
        continue;
      }

      const currentSub = getSubIndex(resource);
      const existingSub = getSubIndex(existing);

      // Keep resource with smaller sub-index
      if (currentSub === 0 || (currentSub > 0 && currentSub < existingSub)) {
        seen.set(mainIndex, resource);
      }
    }

    return [...seen.values()];
  }

  private sortResourcesByIndex(resources: SmartSearchResource[]): any[] {
    return resources.sort((a, b) => {
      return this.getResourceIndex(a) - this.getResourceIndex(b);
    });
  }

  // ========== Reference Replacement ==========

  private replaceReferencesWithButtons(container: HTMLElement): void {
    if (!this.result?.resources) {
      return;
    }

    // Regex: captures the exact index inside brackets, e.g. [3], [3.2]
    const refRegex = /\[(?<ref>\d+(?:\.\d+)?)\]/g;

    // Map synthesis_index → resource
    const resourceMap = new Map(this.result.resources.map((resource: SmartSearchResource) => [resource.synthesis_index ?? '', resource]));

    // Collect all text nodes inside the container
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    const textNodes: Text[] = [];

    let node: Node | null;
    while ((node = walker.nextNode())) {
      if (node.textContent && refRegex.test(node.textContent)) {
        textNodes.push(node as Text);
      }
      refRegex.lastIndex = 0; // reset regex state
    }

    // Replace references in each text node
    textNodes.forEach(textNode => {
      const text = textNode.textContent ?? '';
      const fragment = document.createDocumentFragment();

      let lastIndex = 0;
      let match: RegExpExecArray | null;

      refRegex.lastIndex = 0;

      while ((match = refRegex.exec(text)) !== null) {
        const ref = match.groups!['ref']; // e.g. "3" or "3.2"
        const fullRef = match[0]; // e.g. "[3.2]"
        const start = match.index;

        // Append text BEFORE the reference
        if (start > lastIndex) {
          fragment.appendChild(document.createTextNode(text.slice(lastIndex, start)));
        }

        const resource = resourceMap.get(ref);

        if (resource) {
          // Valid reference → insert button
          const button = this.createButtonElement(resource.doc_id, resource, ref);
          if (button) {
            fragment.appendChild(button);
          }
        }
        // Invalid reference → append nothing → reference disappears
        lastIndex = start + fullRef.length;
      }

      // Append text AFTER the last reference
      if (lastIndex < text.length) {
        fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
      }
      // Replace original text node with generated fragment
      textNode.replaceWith(fragment);
    });
  }

  private createButtonElement(docId: string, resource: SmartSearchResource, index: string | number): HTMLElement | null {
    const button = this.renderer.createElement('button');
    this.renderer.addClass(button, 'source-reference-btn');
    this.renderer.addClass(button, 'reference-badge');
    this.renderer.addClass(button, 'source-reference-btn-positioned');
    this.renderer.setAttribute(button, 'data-doc-id', docId);
    this.renderer.setAttribute(button, 'data-url', resource.url ?? '');
    const textNode = this.renderer.createText(String(index));
    this.renderer.appendChild(button, textNode);

    const title = resource.title ?? '';
    if (title) {
      const tooltip = this.createTooltipElement(
        this.removeEmTags(title),
        resource.publication_date ? this.formatDate(resource.publication_date) : '',
        resource.source ?? null,
        resource.document_type ?? '',
        resource.excerpt ?? ''
      );
      if (tooltip) {
        this.renderer.appendChild(button, tooltip);
      }
    }

    return button;
  }

  private createTooltipElement(
    title: string,
    date: string,
    source: string | null,
    documentType: string,
    excerpt: string
  ): HTMLElement | null {
    const tooltip = this.renderer.createElement('div');
    this.renderer.addClass(tooltip, 'source-reference-tooltip');
    this.renderer.addClass(tooltip, 'smart-search-tooltip');
    this.renderer.addClass(tooltip, 'smart-search-tooltip-positioned');

    const titleDiv = this.renderer.createElement('div');
    this.renderer.addClass(titleDiv, 'tooltip-title');
    this.renderer.setProperty(titleDiv, 'innerHTML', title);
    this.renderer.appendChild(tooltip, titleDiv);

    const metaDiv = this.renderer.createElement('div');
    this.renderer.addClass(metaDiv, 'tooltip-meta');

    if (date) {
      const dateDiv = this.renderer.createElement('div');
      this.renderer.addClass(dateDiv, 'tooltip-date');
      this.renderer.addClass(dateDiv, 'tooltip-date-visible');

      const dateIcon = this.renderer.createElement('span');
      this.renderer.addClass(dateIcon, 'icon-placeholder');
      this.renderer.setAttribute(dateIcon, 'data-icon', 'calendar-basic-600');
      this.renderer.appendChild(dateDiv, dateIcon);

      const dateSpan = this.renderer.createElement('span');
      const dateText = this.renderer.createText(date);
      this.renderer.appendChild(dateSpan, dateText);
      this.renderer.appendChild(dateDiv, dateSpan);

      this.renderer.appendChild(metaDiv, dateDiv);
    }

    if (source && documentType !== 'admin_doc') {
      const sourceDiv = this.renderer.createElement('div');
      this.renderer.addClass(sourceDiv, 'tooltip-source');
      this.renderer.addClass(sourceDiv, 'tooltip-source-visible');

      const sourceIcon = this.renderer.createElement('span');
      this.renderer.addClass(sourceIcon, 'icon-placeholder');
      this.renderer.setAttribute(sourceIcon, 'data-icon', 'docs-basic-600');
      this.renderer.appendChild(sourceDiv, sourceIcon);

      const sourceSpan = this.renderer.createElement('span');
      const sourceText = this.renderer.createText(source);
      this.renderer.appendChild(sourceSpan, sourceText);
      this.renderer.appendChild(sourceDiv, sourceSpan);

      this.renderer.appendChild(metaDiv, sourceDiv);
    }

    this.renderer.appendChild(tooltip, metaDiv);

    if (excerpt) {
      const normalizedExcerpt = excerpt.replaceAll(/\s+/g, ' ').replaceAll(/>\s+</g, '><').trim();

      const excerptDiv = this.renderer.createElement('div');
      this.renderer.addClass(excerptDiv, 'tooltip-excerpt');
      this.renderer.addClass(excerptDiv, 'tooltip-excerpt-visible');
      this.renderer.setProperty(excerptDiv, 'innerHTML', normalizedExcerpt);
      this.renderer.appendChild(tooltip, excerptDiv);
    }

    return tooltip;
  }

  private convertSynthesisStyles(html: string): string {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    tempDiv.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach(heading => {
      const span = document.createElement('span');
      span.className = 'title';
      span.innerHTML = heading.innerHTML;
      heading.parentNode?.replaceChild(span, heading);
    });

    tempDiv.querySelectorAll('p').forEach(p => {
      const span = document.createElement('span');
      span.className = 'text';
      span.innerHTML = p.innerHTML;
      p.parentNode?.replaceChild(span, p);
    });

    return tempDiv.innerHTML;
  }

  // ========== Tooltip Management ==========

  private setupSourceTooltips(): void {
    setTimeout(() => {
      const sourceWrappers = document.querySelectorAll('.right-side .source-link-wrapper:not([data-tooltip-setup])');

      sourceWrappers.forEach(wrapper => {
        const linkWrapper = wrapper as HTMLElement;
        const tooltip = linkWrapper.querySelector('.source-reference-tooltip') as HTMLElement;
        if (!tooltip) {
          return;
        }

        this.configureTooltip(linkWrapper, tooltip);
        this.registerTooltipHandlers(linkWrapper, tooltip);
      });
    }, 0);
  }

  private configureTooltip(linkWrapper: HTMLElement, tooltip: HTMLElement): void {
    linkWrapper.dataset['tooltipSetup'] = 'true';
    if (!tooltip.classList.contains('smart-search-tooltip')) {
      tooltip.classList.add('smart-search-tooltip', 'smart-search-tooltip-positioned');
    }

    if (tooltip.parentElement !== document.body) {
      this.renderer.appendChild(document.body, tooltip);
    }
  }

  private registerTooltipHandlers(linkWrapper: HTMLElement, tooltip: HTMLElement): void {
    const updateTooltipPosition = () => this.updateTooltipPosition(linkWrapper, tooltip);
    const showTooltip = () => {
      updateTooltipPosition();
      tooltip.classList.add('smart-search-tooltip-visible');
    };
    const hideTooltip = () => tooltip.classList.remove('smart-search-tooltip-visible');
    const scrollHandler = () => {
      if (tooltip.classList.contains('smart-search-tooltip-visible')) {
        updateTooltipPosition();
      }
    };

    linkWrapper.addEventListener('mouseenter', showTooltip);
    linkWrapper.addEventListener('mouseleave', hideTooltip);
    globalThis.addEventListener('scroll', scrollHandler, true);
    globalThis.addEventListener('resize', updateTooltipPosition);

    this.tooltipHandlers.push({
      button: linkWrapper,
      tooltip,
      showTooltip,
      hideTooltip,
      scrollHandler,
      resizeHandler: updateTooltipPosition,
    });
  }

  private updateTooltipPosition(linkWrapper: HTMLElement, tooltip: HTMLElement): void {
    const rect = linkWrapper.getBoundingClientRect();
    const windowHeight = globalThis.innerHeight;
    const windowWidth = globalThis.innerWidth;
    const tooltipWidth = 360;

    tooltip.classList.add('smart-search-tooltip-measuring');
    const tooltipHeight = tooltip.getBoundingClientRect().height || 200;
    tooltip.classList.remove('smart-search-tooltip-measuring');

    const spaceLeft = rect.left;
    const spaceRight = windowWidth - rect.left;
    const spaceBelow = windowHeight - rect.top;

    let top = rect.top;
    if (spaceBelow < tooltipHeight) {
      top = Math.max(8, windowHeight - tooltipHeight - 8);
    }

    const left = spaceLeft < tooltipWidth + 8 && spaceRight > tooltipWidth + 8 ? rect.right + 8 : rect.left - tooltipWidth - 8;

    tooltip.style.left = `${Math.max(8, Math.min(left, windowWidth - tooltipWidth - 8))}px`;
    tooltip.style.top = `${top}px`;
  }

  private setupSynthesisTooltips(container: HTMLElement): void {
    setTimeout(() => {
      container.querySelectorAll('.source-reference-btn').forEach(button => {
        const btn = button as HTMLElement;
        const tooltip = btn.querySelector('.source-reference-tooltip') as HTMLElement;
        if (!tooltip) {
          return;
        }

        this.replaceIconPlaceholders(tooltip);
        this.configureTooltip(btn, tooltip);
        this.registerTooltipHandlers(btn, tooltip);
      });
    }, 0);
  }

  private replaceIconPlaceholders(tooltip: HTMLElement): void {
    const placeholders = tooltip.querySelectorAll('.icon-placeholder');
    placeholders.forEach(placeholder => {
      const iconName = (placeholder as HTMLElement).dataset['icon'];
      if (iconName) {
        const iconUrl = this.matIconRegistry.getNamedSvgIcon(iconName);
        if (iconUrl) {
          iconUrl.subscribe((svg: SVGElement) => {
            const svgElement = svg.cloneNode(true) as SVGElement;
            svgElement.setAttribute('width', '16');
            svgElement.setAttribute('height', '16');
            svgElement.style.width = '16px';
            svgElement.style.height = '16px';
            this.renderer.insertBefore(placeholder.parentNode as HTMLElement, svgElement, placeholder);
            this.renderer.removeChild(placeholder.parentNode as HTMLElement, placeholder);
          });
        }
      }
    });
  }

  private handleButtonClick = (event: MouseEvent): void => {
    const target = event.target as HTMLElement;
    if (target.classList.contains('source-reference-btn')) {
      const url = target.dataset['url'];
      if (url) {
        event.preventDefault();
        this.openUrl(url);
      }
    }
  };

  // ========== Export Functions ==========

  getExportToastTitle(): string {
    if (this.exportType === 'docx') {
      return 'search.smart-search-result.awaiting-export-toast-title-word';
    }
    return 'search.smart-search-result.awaiting-export-toast-title-pdf';
  }

  private showExportToast(template: TemplateRef<any>): void {
    this.toastMessageStackService.show(template, {
      classname: this.className + ' toast-shape',
    });
  }

  private removeExportToast(): void {
    if (!this.result?.id) {
      return;
    }
    this.toastMessageStackService.removeByClassName('toast-export-in-progress');
    this.toastMessageStackService.removeByClassName(`t-e-i-p-${this.result.id}`);
    this.toastMessageStackService.removeByClassName(`l-t-${this.result.id}`);
  }

  stopExportAndRemoveExportToast(): void {
    this.removeExportToast();
  }

  private manageApiError(): void {
    this.removeExportToast();
    this.toastMessageStackService.show(this.errorInExport, {
      autohide: true,
      classname: 'error-toast toast-shape',
    });
  }

  async exportFile(type: 'pdf' | 'docx'): Promise<void> {
    if (!this.result?.id) return;

    this.exportType = type;
    this.showExportToast(this.synthesisExportToast);

    try {
      const response = await firstValueFrom(this.apiExportService.getSmartSearchFileBlob(this.result.id, this.exportType));
      this.exportService.saveDceToComputer(response);
      this.trackSmartSearch(EventTypeName.SMART_SEARCH_DOWNLOAD_TRIGGERED, { export_type: type });
    } catch {
      this.manageApiError();
    } finally {
      this.stopExportAndRemoveExportToast();
    }
  }

  // ========== Tracking ==========

  private trackSmartSearch(eventType: EventTypeName, extra?: Partial<Record<string, any>>) {
    const body = {
      event_type: eventType,
      event_timestamp: new Date().toISOString(),
      user_id: localStorage.getItem('user_id'),
      email: localStorage.getItem('email'),
      group_account_id: localStorage.getItem('group_account_id'),
      task_id: this.taskId || '',
      ...extra,
    };

    this.userTrackerService.track(body).pipe(first()).subscribe();
  }

  // ========== Cleanup ==========

  private resetState(): void {
    this.errorTitle = null;
    this.errorContent = null;
    this.result = null;
    this.taskId = null;
    this.resourceTypes.forEach(type => {
      this.resources[type].documents = [];
      this.resources[type].showAllDocuments = false;
    });
  }

  private updateFilteredResources(): void {
    this.resourceTypes.forEach(type => {
      this.resources[type].documents = this.getFilteredResources(type);
    });
  }

  private cleanup(): void {
    this.stopBlinking();
    document.title = this.originalTitle;
    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
      this.visibilityChangeHandler = undefined;
    }
    this.queryParamsSubscription?.unsubscribe();
    this.websocket?.close();

    if (this.synthesisContainer?.nativeElement) {
      this.synthesisContainer.nativeElement.removeEventListener('click', this.handleButtonClick);
    }

    this.tooltipHandlers.forEach(({ button, tooltip, showTooltip, hideTooltip, scrollHandler, resizeHandler }) => {
      button.removeEventListener('mouseenter', showTooltip);
      button.removeEventListener('mouseleave', hideTooltip);
      globalThis.removeEventListener('scroll', scrollHandler, true);
      globalThis.removeEventListener('resize', resizeHandler);
      tooltip.remove();
    });

    this.tooltipHandlers = [];
  }
}
